
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthReadiness } from "@/hooks/useAuthReadiness";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfileFormData, UserPreferences, PrivacySettings } from "@/types/profile";

interface SimpleProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  bio: string | null;
  avatar_url: string | null;
  cover_photo_url: string | null;
  date_of_birth: string | null;
  gender: string | null;
  account_status: string | null;
  timezone: string | null;
  language: string | null;
  preferences: UserPreferences;
  privacy_settings: PrivacySettings;
  social_links: Record<string, string>;
  notification_preferences: Record<string, boolean>;
  two_factor_enabled: boolean | null;
  last_password_change: string | null;
  created_at: string;
  updated_at: string;
  billing_addresses: any;
  shipping_addresses: any;
  role: string;
}

// Expanded update profile data interface to include all possible fields
interface UpdateProfileData {
  full_name?: string;
  phone?: string;
  email?: string;
  bio?: string;
  date_of_birth?: string;
  gender?: string;
  timezone?: string;
  language?: string;
  social_links?: any;
  preferences?: any;
  privacy_settings?: any;
  notification_preferences?: any;
  two_factor_enabled?: boolean;
  last_password_change?: string;
  avatar_url?: string;
  cover_photo_url?: string;
  account_status?: string;
}

const parseJsonField = <T>(value: any, defaultValue: T): T => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...defaultValue, ...value } as T;
  }
  return defaultValue;
};

const getDefaultPreferences = (): UserPreferences => ({
  theme: 'system',
  notifications_email: true,
  notifications_push: true,
  newsletter_subscription: false,
  accent_color: 'blue',
  font_size: 'medium',
  layout_density: 'comfortable',
  animations_enabled: true,
  high_contrast: false,
  color_temperature: 'neutral',
  border_radius: 'rounded',
  sidebar_style: 'modern',
  enhanced_contrast: false,
  glass_effect: false,
});

const getDefaultPrivacySettings = (): PrivacySettings => ({
  profile_visibility: 'public',
  email_visibility: 'private',
  activity_visibility: 'friends',
  show_email: false,
  show_phone: false,
  show_activity: true,
});

export const useSimpleProfile = () => {
  const { user, loading: authLoading, canQueryUserData } = useAuthReadiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['simple-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      
      // Transform the data with proper type casting
      const transformedProfile: SimpleProfile = {
        ...data,
        preferences: parseJsonField(data.preferences, getDefaultPreferences()),
        privacy_settings: parseJsonField(data.privacy_settings, getDefaultPrivacySettings()),
        social_links: parseJsonField(data.social_links, {}),
        notification_preferences: parseJsonField(data.notification_preferences, {
          login_notifications: true,
          order_updates: true,
          email_marketing: true,
          product_recommendations: true,
        }),
      };
      
      return transformedProfile;
    },
    enabled: canQueryUserData,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updatedData: UpdateProfileData) => {
      if (!user?.id) throw new Error('No user ID');
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updatedData)
        .eq('id', user.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simple-profile'] });
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Computed formData property
  const formData: ProfileFormData = {
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    email: profile?.email || user?.email || '',
    bio: profile?.bio || '',
    date_of_birth: profile?.date_of_birth || '',
    gender: profile?.gender || '',
    timezone: profile?.timezone || 'UTC',
    language: profile?.language || 'en',
    social_links: profile?.social_links || {},
  };

  const updateProfile = (data: UpdateProfileData) => {
    updateProfileMutation.mutate(data);
  };

  return {
    profile,
    formData,
    isLoading: authLoading || isLoading,
    isUpdating: updateProfileMutation.isPending,
    error,
    updateProfile,
    refetch,
  };
};
